import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { RiFilterLine, RiSearchLine } from '@remixicon/react';
import type { ParsedTable, TableCell } from '../utils/parse-table';
import { styles } from './interactive-table-view-styles';
import { ScrollableDropdown } from './scrollable-dropdown';

type SortDirection = 'asc' | 'desc' | null;

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
// Columns are only given a filter when they have more than one distinct
// value but not too many - e.g. Status/Type, not free-text Title.
const MAX_FILTERABLE_DISTINCT_VALUES = 10;
const MIN_COLUMN_WIDTH = 60;
const MAX_DEFAULT_COLUMN_WIDTH = 360;
const CHAR_WIDTH_PX = 7.5;
const CELL_PADDING_PX = 32;
// Reserve room for the sort arrow (" ▲"/" ▼") regardless of whether the
// column is currently sorted, so clicking it to sort doesn't suddenly make
// the header text collide with the resize handle.
const SORT_ARROW_RESERVED_PX = 20;
const ROW_NUMBER_COLUMN_WIDTH = 50;

// Cells can be undefined for rows shorter than the header (uneven row
// lengths from a malformed or colspan/rowspan-using source table) - treated
// as an empty cell rather than crashing the sort.
function compareCells(
  a: TableCell | undefined,
  b: TableCell | undefined,
): number {
  const aText = a?.text ?? '';
  const bText = b?.text ?? '';
  if (aText !== '' && bText !== '') {
    const aNum = Number(aText);
    const bNum = Number(bText);
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
      return aNum - bNum;
    }
  }
  return aText.localeCompare(bText, undefined, { sensitivity: 'base' });
}

function nextDirection(current: SortDirection): SortDirection {
  if (current === null) return 'asc';
  if (current === 'asc') return 'desc';
  return null;
}

function sortArrow(isSorted: boolean, direction: SortDirection): string {
  if (!isSorted) return '';
  return direction === 'asc' ? ' ▲' : ' ▼';
}

function sortAriaValue(
  isSorted: boolean,
  direction: SortDirection,
): 'ascending' | 'descending' | 'none' {
  if (!isSorted) return 'none';
  return direction === 'asc' ? 'ascending' : 'descending';
}

export const InteractiveTableView = ({ headers, rows }: ParsedTable) => {
  const [search, setSearch] = useState('');
  // Each column can have multiple selected values (OR'd together within the
  // column); an empty/missing array means that column isn't filtered.
  const [columnFilters, setColumnFilters] = useState<Record<number, string[]>>(
    {},
  );
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [sort, setSort] = useState<{
    column: number;
    direction: SortDirection;
  }>({
    column: -1,
    direction: null,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [columnWidths, setColumnWidths] = useState<Record<number, number>>({});
  const resizingRef = useRef<{
    columnIndex: number;
    startX: number;
    startWidth: number;
    moved: boolean;
  } | null>(null);
  // Manual double-click detection for the resize handle: native 'dblclick'
  // isn't reliable here since the handle also drives drag-resize on the
  // same mousedown/mouseup pair, which can suppress the browser's own
  // double-click synthesis. Tracking clicks ourselves avoids depending on
  // that timing-sensitive native.
  const lastHandleClickRef = useRef<{
    columnIndex: number;
    time: number;
  } | null>(null);

  // Distinct values per column, computed from the full dataset (not the
  // currently-filtered rows) so filter options stay stable.
  const columnDistinctValues = useMemo(() => {
    return headers.map((_, columnIndex) => {
      const values = new Set<string>();
      rows.forEach(row => values.add(row[columnIndex]?.text ?? ''));
      return Array.from(values).sort((a, b) => a.localeCompare(b));
    });
  }, [headers, rows]);

  const filterableColumns = useMemo(() => {
    const set = new Set<number>();
    columnDistinctValues.forEach((values, columnIndex) => {
      if (
        values.length > 1 &&
        values.length <= MAX_FILTERABLE_DISTINCT_VALUES
      ) {
        set.add(columnIndex);
      }
    });
    return set;
  }, [columnDistinctValues]);

  const activeFilterEntries = useMemo(
    () =>
      Object.entries(columnFilters).flatMap(([columnIndex, values]) =>
        values.map(value => ({ columnIndex: Number(columnIndex), value })),
      ),
    [columnFilters],
  );

  // Close the filter panel on outside click, same pattern as the
  // per-dropdown one above but for the panel as a whole. Uses
  // composedPath() rather than e.target for the same shadow-DOM
  // retargeting reason documented on ScrollableDropdown's handler.
  useEffect(() => {
    if (!isFilterPanelOpen) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        filterPanelRef.current &&
        !e.composedPath().includes(filterPanelRef.current)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFilterPanelOpen]);

  // Default width per column sized to its actual content (longest value or
  // header text), not an equal split of the table width - a short "Status"
  // column shouldn't get the same width as a long "Title" column. Still
  // just a starting point: users can drag to override any column.
  //
  // Columns whose content is long enough to hit MAX_DEFAULT_COLUMN_WIDTH
  // are already being truncated by design (that's what the cap is for) -
  // for those, letting the user shrink further down to half that width is
  // just "more truncation," not a functional problem, so they get a lower
  // resize floor than normal columns. Normal (uncapped) columns keep the
  // strict "can't shrink below your own natural width" floor, since for
  // those any further shrinking would newly start truncating content that
  // currently fits.
  const { defaultColumnWidths, minColumnWidths } = useMemo(() => {
    const defaults: number[] = [];
    const minimums: number[] = [];
    headers.forEach((header, columnIndex) => {
      let maxLen = header.text.length;
      rows.forEach(row => {
        const len = row[columnIndex]?.text.length ?? 0;
        if (len > maxLen) maxLen = len;
      });
      const naturalPx =
        Math.round(maxLen * CHAR_WIDTH_PX) +
        CELL_PADDING_PX +
        SORT_ARROW_RESERVED_PX;
      const isLongColumn = naturalPx > MAX_DEFAULT_COLUMN_WIDTH;
      const width = Math.min(
        MAX_DEFAULT_COLUMN_WIDTH,
        Math.max(MIN_COLUMN_WIDTH, naturalPx),
      );
      defaults.push(width);
      minimums.push(
        isLongColumn
          ? Math.max(MIN_COLUMN_WIDTH, Math.round(MAX_DEFAULT_COLUMN_WIDTH / 2))
          : width,
      );
    });
    return { defaultColumnWidths: defaults, minColumnWidths: minimums };
  }, [headers, rows]);

  const effectiveColumnWidths = useMemo(
    () => defaultColumnWidths.map((width, i) => columnWidths[i] ?? width),
    [defaultColumnWidths, columnWidths],
  );

  // Mirrored into a ref so the stable drag-handler closures below (created
  // once via useRef) can always read the latest values without needing to
  // be recreated - safe since it's only read asynchronously during a later
  // mousemove, never during this render itself.
  const minColumnWidthsRef = useRef<number[]>(minColumnWidths);
  minColumnWidthsRef.current = minColumnWidths;

  const totalTableWidth = useMemo(
    () =>
      ROW_NUMBER_COLUMN_WIDTH +
      effectiveColumnWidths.reduce((sum, w) => sum + w, 0),
    [effectiveColumnWidths],
  );

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter(row => {
      if (
        needle &&
        !row.some(cell => cell.text.toLowerCase().includes(needle))
      ) {
        return false;
      }
      return Object.entries(columnFilters).every(([columnIndex, values]) => {
        if (values.length === 0) return true;
        return values.includes(row[Number(columnIndex)]?.text ?? '');
      });
    });
  }, [rows, search, columnFilters]);

  const sortedRows = useMemo(() => {
    if (sort.column < 0 || !sort.direction) return filteredRows;
    const { column, direction } = sort;
    return [...filteredRows].sort((a, b) => {
      const cmp = compareCells(a[column], b[column]);
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [filteredRows, sort]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));

  // Keep the current page in range whenever the result set or page size
  // changes (e.g. a search narrows the rows down below the current page).
  useEffect(() => {
    setPage(prev => Math.min(prev, pageCount));
  }, [pageCount]);

  useEffect(() => {
    setPage(1);
  }, [search, columnFilters]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const handleHeaderClick = (columnIndex: number) => {
    setSort(prev => ({
      column: columnIndex,
      direction:
        prev.column === columnIndex ? nextDirection(prev.direction) : 'asc',
    }));
  };

  const toggleFilterValue = (columnIndex: number, value: string) => {
    setColumnFilters(prev => {
      const current = prev[columnIndex] ?? [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [columnIndex]: next };
    });
  };

  const clearColumnFilter = (columnIndex: number) => {
    setColumnFilters(prev => ({ ...prev, [columnIndex]: [] }));
  };

  const removeFilterValue = (columnIndex: number, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnIndex]: (prev[columnIndex] ?? []).filter(v => v !== value),
    }));
  };

  const clearAllFilters = () => {
    setColumnFilters({});
  };

  const resetColumnWidth = (columnIndex: number) => {
    setColumnWidths(prev => {
      if (!(columnIndex in prev)) return prev;
      const next = { ...prev };
      delete next[columnIndex];
      return next;
    });
  };

  // Stable function identities (created once) so addEventListener and
  // removeEventListener always refer to the same underlying listener,
  // regardless of how many times this component has re-rendered since the
  // drag started. Fresh drag state is read via resizingRef at call time,
  // so there's no stale-closure risk despite the refs never changing.
  const handleResizeMoveRef = useRef((e: MouseEvent) => {
    const state = resizingRef.current;
    if (!state) return;
    const delta = e.clientX - state.startX;
    if (Math.abs(delta) > 2) state.moved = true;
    // Normal columns can't shrink below their own natural width (avoids
    // ever colliding with the resize handle / sort arrow); long free-text
    // columns like "Title" get a lower floor since they're already being
    // truncated by design at their default width.
    const minWidth = Math.max(
      MIN_COLUMN_WIDTH,
      minColumnWidthsRef.current[state.columnIndex] ?? MIN_COLUMN_WIDTH,
    );
    const newWidth = Math.max(minWidth, state.startWidth + delta);
    setColumnWidths(prev => ({ ...prev, [state.columnIndex]: newWidth }));
  });
  const handleResizeEndRef = useRef(() => {
    const state = resizingRef.current;
    resizingRef.current = null;
    globalThis.removeEventListener('mousemove', handleResizeMoveRef.current);
    globalThis.removeEventListener('mouseup', handleResizeEndRef.current);

    if (!state || state.moved) return;
    // A plain click (no drag) on the handle - check if it's the second one
    // within the double-click window, and if so reset that column's width.
    const now = Date.now();
    const last = lastHandleClickRef.current;
    if (last?.columnIndex === state.columnIndex && now - last.time < 400) {
      resetColumnWidth(state.columnIndex);
      lastHandleClickRef.current = null;
    } else {
      lastHandleClickRef.current = {
        columnIndex: state.columnIndex,
        time: now,
      };
    }
  });

  const handleResizeStart = (
    columnIndex: number,
    e: ReactMouseEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const headerCellEl = (e.target as HTMLElement).closest(
      '[role="columnheader"]',
    ) as HTMLElement | null;
    const startWidth = headerCellEl?.getBoundingClientRect().width ?? 150;
    resizingRef.current = {
      columnIndex,
      startX: e.clientX,
      startWidth,
      moved: false,
    };
    globalThis.addEventListener('mousemove', handleResizeMoveRef.current);
    globalThis.addEventListener('mouseup', handleResizeEndRef.current);
  };

  useEffect(() => {
    const handleResizeMove = handleResizeMoveRef.current;
    const handleResizeEnd = handleResizeEndRef.current;
    return () => {
      globalThis.removeEventListener('mousemove', handleResizeMove);
      globalThis.removeEventListener('mouseup', handleResizeEnd);
    };
  }, []);

  return (
    <div style={styles.wrapper}>
      <div style={styles.toolbar}>
        <div style={styles.searchWrapper}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search table…"
            aria-label="Search table"
            style={styles.search}
          />
          <span style={styles.searchIcon}>
            <RiSearchLine size={16} aria-hidden="true" />
          </span>
        </div>
        <div ref={filterPanelRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-expanded={isFilterPanelOpen}
            onClick={() => setIsFilterPanelOpen(prev => !prev)}
            style={{
              ...styles.filterButton,
              ...(activeFilterEntries.length > 0
                ? styles.filterButtonActive
                : {}),
            }}
          >
            <RiFilterLine size={14} aria-hidden="true" />
            Filter
            {activeFilterEntries.length > 0
              ? ` (${activeFilterEntries.length})`
              : ''}
          </button>
          {isFilterPanelOpen && (
            <div style={styles.filterPanel}>
              {filterableColumns.size === 0 ? (
                <div style={styles.filterPanelEmpty}>
                  No filterable columns on this table.
                </div>
              ) : (
                Array.from(filterableColumns).map(columnIndex => (
                  <div key={columnIndex} style={styles.filterPanelRow}>
                    <span style={styles.filterPanelLabel}>
                      {headers[columnIndex].text}
                    </span>
                    <ScrollableDropdown
                      label={headers[columnIndex].text}
                      selectedValues={columnFilters[columnIndex] ?? []}
                      options={columnDistinctValues[columnIndex]}
                      onToggle={value => toggleFilterValue(columnIndex, value)}
                      onClear={() => clearColumnFilter(columnIndex)}
                    />
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      {activeFilterEntries.length > 0 && (
        <div style={styles.filterChipsRow}>
          {activeFilterEntries.map(({ columnIndex, value }) => (
            <span key={`${columnIndex}:${value}`} style={styles.chip}>
              {headers[columnIndex].text}: {value || '(empty)'}
              <button
                type="button"
                aria-label={`Remove ${headers[columnIndex].text}: ${
                  value || '(empty)'
                } filter`}
                style={styles.chipRemove}
                onClick={() => removeFilterValue(columnIndex, value)}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            style={styles.clearAllLink}
            onClick={clearAllFilters}
          >
            Clear all
          </button>
        </div>
      )}
      <div style={styles.tableScroll}>
        <div
          role="table"
          style={{ ...styles.table, width: '100%', minWidth: totalTableWidth }}
        >
          <div role="rowgroup" style={styles.headerGroup}>
            <div role="row" style={styles.row}>
              <div
                role="columnheader"
                style={{
                  ...styles.rowNumberHeaderCell,
                  width: ROW_NUMBER_COLUMN_WIDTH,
                }}
              >
                #
              </div>
              {headers.map((header, columnIndex) => {
                const isSorted = Boolean(
                  sort.column === columnIndex && sort.direction,
                );
                const arrow = sortArrow(isSorted, sort.direction);
                // The last column is left width: auto (just a minWidth floor)
                // unless the user has explicitly dragged it - table-layout:
                // fixed only distributes leftover space into columns that
                // don't already have an explicit width, so leaving exactly
                // one column unconstrained is what lets the table stretch to
                // fill its container instead of stopping at its own content
                // width with dead space to the right of it.
                const isLastColumn = columnIndex === headers.length - 1;
                const hasManualWidth = columnWidths[columnIndex] !== undefined;
                const widthStyle =
                  isLastColumn && !hasManualWidth
                    ? { minWidth: minColumnWidths[columnIndex] }
                    : { width: effectiveColumnWidths[columnIndex] };
                return (
                  <div
                    // Columns are positional table data with no natural
                    // unique id, and don't reorder independently within a
                    // mounted table - the index is a stable key here.
                    key={columnIndex} // NOSONAR
                    role="columnheader"
                    aria-sort={sortAriaValue(isSorted, sort.direction)}
                    style={{
                      ...styles.headerCell,
                      ...widthStyle,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleHeaderClick(columnIndex)}
                      style={styles.headerLabel}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: header.html + arrow }}
                    />
                    {/* Mouse-drag-only resize affordance (role="separator" has
                      no accessible keyboard-resize equivalent implemented -
                      double-click-to-reset is the keyboard-free fallback).
                      Deliberately not a native <hr>: this is a draggable
                      control with an onMouseDown handler, not a semantic
                      thematic break - <hr> has no interaction model and
                      would misrepresent what this element does. */}
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <div
                      role="separator" // NOSONAR: see rationale above, <hr> would misrepresent this as non-interactive
                      aria-orientation="vertical"
                      aria-label={`Resize ${header.text} column`}
                      title="Drag to resize, double-click to reset"
                      onMouseDown={e => handleResizeStart(columnIndex, e)}
                      style={styles.resizeHandle}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div role="rowgroup" style={styles.bodyGroup}>
            {pagedRows.map((row, rowIndex) => (
              <div
                role="row"
                // Parsed table rows have no natural unique id (arbitrary
                // markdown source data), so the index is the only stable key.
                key={rowIndex} // NOSONAR
                style={styles.row}
              >
                <div role="cell" style={styles.rowNumberCell}>
                  {(page - 1) * pageSize + rowIndex + 1}
                </div>
                {row.map((cell, columnIndex) => (
                  <div
                    key={columnIndex} // NOSONAR: see column-key rationale above
                    role="cell"
                    title={cell.text}
                    style={styles.cell}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: cell.html }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      {sortedRows.length === 0 && (
        <div style={styles.emptyState}>No matching rows.</div>
      )}
      {sortedRows.length > 0 && (
        <div style={styles.pagination}>
          <span>Rows per page:</span>
          <select
            aria-label="Rows per page"
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            style={styles.pageSizeSelect}
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span>
            Page {page} of {pageCount} ({sortedRows.length} rows)
          </span>
          <button
            type="button"
            aria-label="Previous page"
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={page <= 1}
            style={{
              ...styles.pageButton,
              ...(page <= 1 ? styles.pageButtonDisabled : {}),
            }}
          >
            {'< Prev'}
          </button>
          <button
            type="button"
            aria-label="Next page"
            onClick={() => setPage(prev => Math.min(pageCount, prev + 1))}
            disabled={page >= pageCount}
            style={{
              ...styles.pageButton,
              ...(page >= pageCount ? styles.pageButtonDisabled : {}),
            }}
          >
            {'Next >'}
          </button>
        </div>
      )}
    </div>
  );
};
